import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import CustomNumberInput from "../../../../components/CustomNumberInput";
import CustomTextInput from "../../../../components/CustomTextInput";
import CustomSingleSelect, { CustomSingleSelectModel } from "../../../../components/CustomSingleSelect";
import Box from "../../../../components/Box";
import { Helpers } from "../../../../helpers/Helpers";
import { PersonnelService } from "../../../../services/PersonnelService";
import { storeActivityIndicator } from "../../../../redux/reducers/authSlice";
import YeniPersonelModal from "./YeniPersonelModal";

/** Mesai ve hammaliye satırlarının ortak alanları; miktar alanı (saat/gün) satır türüne göre değişir. */
export interface PersonelSatiri {
    personnelId: string | undefined
    personnelName: string | undefined
    supplierCompanyName: string | undefined
    description: string | undefined
}

interface Props<T extends PersonelSatiri> {
    baslik: string
    miktarBasligi: string
    birim: string
    satirlar: T[]
    setSatirlar: (_satirlar: T[]) => void
    /** Satırdaki miktar alanının adı: mesaide "hours", hammaliyede "days". */
    miktarAlani: keyof T
    yeniSatir: () => T
}

/**
 * Personel satırı tablosu: Personel (personel tablosundan, "+" ile yeni personel), miktar, pasif Firma, Açıklama.
 * "Mesai Yapan Personel" ve "Hammaliye Personeli" aynı bileşeni kullanır.
 */
const PersonelSatirTablosu = <T extends PersonelSatiri,>({
    baslik,
    miktarBasligi,
    birim,
    satirlar,
    setSatirlar,
    miktarAlani,
    yeniSatir,
}: Props<T>) => {
    const dispatch = useDispatch();

    // Yeni personel penceresinin hangi satır için açıldığı; kapalıyken undefined.
    const [yeniPersonelSatiri, setYeniPersonelSatiri] = useState<number | undefined>(undefined);

    const miktar = (_satir: T) => _satir[miktarAlani] as unknown as string | undefined;

    const satirEkle = () => {
        setSatirlar([...satirlar, yeniSatir()]);
    }

    const satirSil = (_sira: number) => {
        setSatirlar(satirlar.filter((x, i) => i != _sira));
    }

    const satirDegistir = (_sira: number, _degisiklik: Partial<T>) => {
        setSatirlar(satirlar.map((x, i) => i == _sira ? { ...x, ..._degisiklik } : x));
    }

    const personelSec = (_sira: number, _secim: CustomSingleSelectModel | undefined) => {
        satirDegistir(_sira, {
            personnelId: _secim?.value,
            personnelName: _secim?.label,
            // Firma alanı pasif: seçilen personelin firması kendiliğinden yazılır.
            supplierCompanyName: _secim?.additionalValue1,
        } as Partial<T>);
    }

    const yeniPersonelKaydet = async (_adSoyad: string, _firma: CustomSingleSelectModel) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await PersonnelService.addPersonnelWithCompany(_adSoyad, _firma.value);

            if (!response.isSuccess) {
                toast.error(response.message);
                return;
            }

            if (yeniPersonelSatiri != undefined) {
                satirDegistir(yeniPersonelSatiri, {
                    personnelId: response.data.personnelId,
                    personnelName: response.data.nameSurname,
                    supplierCompanyName: response.data.supplierCompanyName,
                } as Partial<T>);
            }

            toast.success('Personel tanımlandı.');
            setYeniPersonelSatiri(undefined);
        } catch (error: any) {
            toast.error(error);
        }
        finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const toplam = satirlar
        .map(x => Helpers.toNumber(miktar(x) ?? '0'))
        .reduce((a, b) => a + b, 0);

    return (
        <React.Fragment>
            <Box className='ebbe-hr m-0 mt-8'>&nbsp;</Box>

            <h5 className="mt-8 mb-5 text-black">{baslik}</h5>

            <div className="table-responsive new-table-container ebbe-table-icerik-boyu">
                <table className="table align-middle table-row-dashed fs-6 gy-3 mb-0" id="kt_table_users">
                    <thead>
                        <tr className="text-start fw-bold text-uppercase gs-0">
                            <th className="text-center" style={{ width: '50px' }}>#</th>
                            <th style={{ width: '26%' }}>Personel</th>
                            <th style={{ width: '15%' }}>{miktarBasligi}</th>
                            <th style={{ width: '24%' }}>Firma</th>
                            <th>Açıklama</th>
                            <th className="text-center" style={{ width: '80px' }}>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            satirlar.length == 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted">
                                        Henüz personel eklenmedi. "Satır Ekle" ile başlayın.
                                    </td>
                                </tr>
                            )
                        }

                        {
                            satirlar.map((item, index) =>
                                <tr key={index}>
                                    <td className="text-center">{index + 1}</td>

                                    <td>
                                        <Box flexDirection="row" alignItems="center">
                                            <Box flex={1}>
                                                <CustomSingleSelect
                                                    selectedValue={item.personnelId && item.personnelName
                                                        ? { label: item.personnelName, value: item.personnelId }
                                                        : undefined}
                                                    onChange={(value) => personelSec(index, value)}
                                                    onMenuOpen={() => PersonnelService.getPersonnelList(undefined, true)}
                                                    menuPortal
                                                    labelName='nameSurname'
                                                    valueName='personnelId'
                                                    additionalValue1Name='supplierCompanyName'
                                                />
                                            </Box>

                                            <Box ml="8px">
                                                <Box
                                                    onClick={() => setYeniPersonelSatiri(index)}
                                                    className="actionIcon p-2 bg-primary"
                                                    justifyContent="center"
                                                    borderRadius="4px"
                                                >
                                                    <i className="text-white fa fa-plus" />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </td>

                                    <td>
                                        <CustomNumberInput
                                            value={miktar(item)}
                                            setValue={(value: string) => satirDegistir(index, { [miktarAlani]: value } as Partial<T>)}
                                        />
                                    </td>

                                    <td>
                                        <CustomTextInput value={item.supplierCompanyName ?? ''} setValue={() => { }} isDisabled />
                                    </td>

                                    <td>
                                        <CustomTextInput
                                            value={item.description}
                                            setValue={(value: string) => satirDegistir(index, { description: value } as Partial<T>)}
                                        />
                                    </td>

                                    <td>
                                        <Box
                                            onClick={() => satirSil(index)}
                                            className="actionIcon p-2 bg-danger"
                                            justifyContent="center"
                                            alignItems="center"
                                            borderRadius="4px"
                                            width="38px"
                                            ml="auto"
                                            mr="auto"
                                        >
                                            <i className="text-white fa fa-trash" />
                                        </Box>
                                    </td>
                                </tr>
                            )
                        }
                    </tbody>
                </table>
            </div>

            <Box flexDirection="row" alignItems="center" mt="12px">
                <Box onClick={satirEkle} className="actionIcon p-2 bg-success" justifyContent="center" flexDirection="row" borderRadius="4px">
                    <Box mr="5px" justifyContent="center">
                        <i className="text-white fa fa-plus" />
                    </Box>
                    <Box justifyContent="center">
                        <span style={{ fontWeight: 600 }} className="text-white">Satır Ekle</span>
                    </Box>
                </Box>

                {
                    satirlar.length > 0 && (
                        <span className="text-muted fs-7 ms-auto">
                            <b className="text-black">{satirlar.length}</b> personel · toplam{' '}
                            <b className="text-black">{toplam.toString().replace('.', ',')}</b> {birim}
                        </span>
                    )
                }
            </Box>

            <YeniPersonelModal
                isShown={yeniPersonelSatiri != undefined}
                onConfirm={yeniPersonelKaydet}
                onClose={() => setYeniPersonelSatiri(undefined)}
            />
        </React.Fragment>
    )
}

export default PersonelSatirTablosu;
